import { Alert, Button, Col, Form, InputGroup, ListGroup, Row } from 'react-bootstrap'
import { useField, useFormikContext, type FieldArrayRenderProps } from 'formik'
import * as Yup from 'yup'
import { v4 as uuidv4 } from 'uuid'
import { useAuth, shouldShowExperimentalFeatures } from '../../auth'
import { FigureType, getColumnValues, HorizontalPositionBase } from '../../common'
import { createSimpleValidation } from '../common'
import { useFormikReset, DirtyEffect } from '../CommonComponents'
import { stripTags } from '../text'
import { isAreaArticleType } from '../ArticleParse'
import { getFigureNumberOfOccupyingRowsFromSetting, nullishValueToValue, TagEditor } from '../../article'
import { getFigureImageUrl, isFigureImageViewable } from './figureImage'
import type { Article, Figure, Layoutable } from '../types'
import type { OrientationValue } from '../../common/types'

type PositionBaseName = 'verticalPositionBase' | 'horizontalPositionBase'
type PositionName = 'verticalPosition' | 'horizontalPosition'

interface EnumValues {
  figureTypes: Record<string, string>
  figureOrientations: Record<string, string>
  textFlowPositions: Record<string, string>
  captionPositions: Record<string, string>
}

interface Enums {
  positionBases: Record<PositionBaseName, Record<string, string>>
  positionUnitName: Record<PositionName, string>
  paragraphStyleName: string
}

interface AvailableFigure {
  id: number | string
  filename: string
  location: string
}

interface FormValues {
  figures: Partial<Figure>[]
  availableFigures?: AvailableFigure[]
}

interface Props {
  inputDisabled: boolean
  arrayHelpers: FieldArrayRenderProps
  layoutable: Layoutable
  article: Article
  enumValues: EnumValues
  enums: Enums
  baseUri: string
}

interface FigureFieldProps {
  index: number
  enumValues: EnumValues
  inputDisabled: boolean
  arrayHelpers: FieldArrayRenderProps
  layoutable: Layoutable
  article: Article
  enums: Enums
  baseUri: string
}

const getDummyId = () => `null-${uuidv4()}`
const INVALID_ID_MESSAGE = '不正なIDです'
const POSITION_ERROR_MESSAGE = '1以上にしてください'

const createNewFigureItem = () => {
  return {
    caption: '',
    captionPosition: 'left',
    figureType: FigureType.Photo,
    filename: '',
    fluid: false,
    horizontalPosition: nullishValueToValue(''),
    horizontalPositionBase: null,
    id: getDummyId(),
    location: 'dummy',
    numberOfOccupyingColumns: '2.0',
    numberOfOccupyingRows: '10.0',
    orientation: 'vertical',
    taggedCaption: '',
    textFlowPosition: 'lower',
    verticalPosition: nullishValueToValue(''),
    verticalPositionBase: null
  }
}

const NoFigures = () => {
  return (
    <Row>
      <Col>写真・図なし</Col>
    </Row>
  )
}

const FigureTypeField = ({
  index,
  enumValues,
  inputDisabled
}: {
  index: number
  enumValues: EnumValues
  inputDisabled: boolean
}) => {
  const [field, meta, helper] = useField({
    name: `figures.${index}.figureType`,
    validate: createSimpleValidation(Yup.string().required())
  })
  const dirty = field.value !== meta.initialValue

  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    helper.setValue((e.target as HTMLSelectElement).value)
  }

  const figureTypeOptions = Object.keys(enumValues.figureTypes).map((k) => {
    return (
      <option key={k} value={k}>
        {enumValues.figureTypes[k]}
      </option>
    )
  })

  return (
    <DirtyEffect dirty={dirty}>
      <Form.Control
        as='select'
        type='text'
        name='figureType'
        size='sm'
        onChange={onChange}
        defaultValue={field.value}
        disabled={inputDisabled}
      >
        {figureTypeOptions}
      </Form.Control>
    </DirtyEffect>
  )
}

const OrientationField = ({
  index,
  layoutable,
  inputDisabled,
  enumValues
}: {
  index: number
  layoutable: Layoutable
  inputDisabled: boolean
  enumValues: EnumValues
}) => {
  const context = useFormikContext<FormValues>()
  const fieldName = `figures.${index}.orientation`
  const [field, meta, helper] = useField({
    name: fieldName,
    validate: createSimpleValidation(Yup.string().required())
  })
  const dirty = field.value !== meta.initialValue

  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const orientation = (e.target as HTMLInputElement).value
    const numberOfOccupyingColumns = context.values.figures[index].numberOfOccupyingColumns
    const numberOfOccupyingRows = getFigureNumberOfOccupyingRowsFromSetting({
      figure: { orientation: orientation as OrientationValue },
      numberOfOccupyingColumns: Number(numberOfOccupyingColumns),
      setting: layoutable.setting
    })
    context.setFieldValue(`figures.${index}.numberOfOccupyingRows`, numberOfOccupyingRows)
    helper.setValue(orientation)
  }

  const orientationChecks = Object.keys(enumValues.figureOrientations).map((k) => {
    return (
      <Form.Check
        id={`${fieldName}-${k}`}
        key={`${fieldName}-${k}`}
        inline
        label={enumValues.figureOrientations[k]}
        type='radio'
        name={`${fieldName}`}
        checked={k === field.value}
        value={k}
        onChange={onChange}
        disabled={inputDisabled}
      />
    )
  })

  return <DirtyEffect dirty={dirty}>{orientationChecks}</DirtyEffect>
}

const NumberOfOccupyingColumnsField = ({
  sm,
  index,
  layoutable,
  inputDisabled
}: {
  sm?: number | string
  index: number
  layoutable: Layoutable
  inputDisabled: boolean
}) => {
  const context = useFormikContext<FormValues>()
  const [field, meta, helper] = useField({
    name: `figures.${index}.numberOfOccupyingColumns`,
    validate: createSimpleValidation(Yup.number().min(1).nullable())
  })
  const dirty = field.value !== meta.initialValue

  const showExperimentalFeatures = shouldShowExperimentalFeatures(useAuth())
  const columnValues = getColumnValues(showExperimentalFeatures)

  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const numberOfOccupyingColumns = (e.target as HTMLSelectElement).value
    const orientation = context.values.figures[index].orientation
    const numberOfOccupyingRows = getFigureNumberOfOccupyingRowsFromSetting({
      figure: { orientation: orientation as OrientationValue },
      numberOfOccupyingColumns: Number(numberOfOccupyingColumns),
      setting: layoutable.setting
    })
    context.setFieldValue(`figures.${index}.numberOfOccupyingRows`, numberOfOccupyingRows)
    helper.setValue(numberOfOccupyingColumns)
  }

  const columnOptions = columnValues.map(({ label, value }, idx) => (
    <option key={idx} value={value}>
      {label}
    </option>
  ))

  return (
    <Form.Group as={Col} sm={sm}>
      <DirtyEffect dirty={dirty}>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            id='columnsInput'
            type='number'
            as='select'
            name='numberOfOccupyingColumns'
            size='sm'
            value={field.value || ''}
            onChange={onChange}
            isInvalid={!!meta.error}
            disabled={inputDisabled}
          >
            <option value=''>--段</option>
            {columnOptions}
          </Form.Control>
          <Form.Control.Feedback type='invalid'>{meta.error}</Form.Control.Feedback>
        </InputGroup>
      </DirtyEffect>
    </Form.Group>
  )
}

const NumberOfOccupyingRowsField = ({
  sm,
  index,
  inputDisabled
}: {
  sm?: number | string
  index: number
  inputDisabled: boolean
}) => {
  const [field, meta, helper] = useField({
    name: `figures.${index}.numberOfOccupyingRows`,
    validate: createSimpleValidation(Yup.number().min(1).nullable())
  })
  const dirty = field.value !== meta.initialValue
  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    helper.setValue((e.target as HTMLInputElement).value)
  }

  return (
    <Form.Group as={Col} sm={sm}>
      <DirtyEffect dirty={dirty}>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            id='rowsInput'
            type='number'
            name='numberOfOccupyingRows'
            size='sm'
            min={1}
            value={field.value || ''}
            onChange={onChange}
            isInvalid={!!meta.error}
            disabled={inputDisabled}
          />
          <InputGroup.Text>行</InputGroup.Text>
          <Form.Control.Feedback type='invalid'>{meta.error}</Form.Control.Feedback>
        </InputGroup>
      </DirtyEffect>
    </Form.Group>
  )
}

const PositionBaseField = ({
  name,
  index,
  enums,
  inputDisabled
}: {
  name: PositionBaseName
  index: number
  enums: Enums
  inputDisabled: boolean
}) => {
  const fieldName = `figures.${index}.${name}`
  const [field, meta, helper] = useField({
    name: fieldName,
    validate: createSimpleValidation(Yup.string().nullable())
  })
  const valueFieldName = name.replace(/Base$/, '')
  const [, , posHelper] = useField({ name: `figures.${index}.${valueFieldName}` })
  const dirty = field.value !== meta.initialValue

  const RELATIVE_HORIZONTAL_POSITION_BASE_TYPES = new Set<string>([
    HorizontalPositionBase.Right,
    HorizontalPositionBase.Left
  ])
  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const newValue = (e.target as HTMLSelectElement).value
    const posValue =
      name === 'horizontalPositionBase' && RELATIVE_HORIZONTAL_POSITION_BASE_TYPES.has(newValue) ? '1' : ''
    posHelper.setValue(posValue)
    helper.setValue(newValue)
  }

  const positionBaseOptions = Object.keys(enums.positionBases[name]).map((k) => (
    <option key={k} value={k}>
      {enums.positionBases[name][k]}
    </option>
  ))

  return (
    <Form.Group>
      <DirtyEffect dirty={dirty}>
        <Form.Control
          as='select'
          type='text'
          name={fieldName}
          size='sm'
          value={field.value || ''}
          onChange={onChange}
          isInvalid={!!meta.error}
          disabled={inputDisabled}
        >
          <option value=''>位置ベースを選択</option>
          {positionBaseOptions}
        </Form.Control>
        <Form.Control.Feedback type='invalid'>{meta.error}</Form.Control.Feedback>
      </DirtyEffect>
    </Form.Group>
  )
}

const PositionField = ({
  name,
  index,
  enums,
  inputDisabled
}: {
  name: PositionName
  index: number
  enums: Enums
  inputDisabled: boolean
}) => {
  const fieldName = `figures.${index}.${name}`
  const [field, meta, helper] = useField({
    name: fieldName,
    validate: createSimpleValidation(Yup.number().min(0, POSITION_ERROR_MESSAGE).nullable(), (value) =>
      value !== '' ? value : null
    )
  })
  const posBaseFieldName = `${name}Base`
  const [posBaseField, ,] = useField({ name: `figures.${index}.${posBaseFieldName}` })

  const inputEditable =
    (name === 'verticalPosition' && !!posBaseField.value) ||
    (name === 'horizontalPosition' && posBaseField.value === 'absolute')
  const dirty = field.value !== meta.initialValue

  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const val = (e.target as HTMLInputElement).value
    helper.setValue(val === '' ? '' : Number(val))
  }

  return (
    <Form.Group>
      <DirtyEffect dirty={dirty}>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            id={`${fieldName}-input`}
            type='number'
            name={fieldName}
            size='sm'
            min={1}
            value={field.value}
            placeholder='ナリ'
            onChange={onChange}
            isInvalid={!!meta.error}
            disabled={inputDisabled || !inputEditable}
          />
          <InputGroup.Text>{enums.positionUnitName[name]}</InputGroup.Text>
          <Form.Control.Feedback type='invalid'>{meta.error}</Form.Control.Feedback>
        </InputGroup>
      </DirtyEffect>
    </Form.Group>
  )
}

const TextFlowPositionField = ({
  index,
  enumValues,
  inputDisabled
}: {
  index: number
  enumValues: EnumValues
  inputDisabled: boolean
}) => {
  const fieldName = `figures.${index}.textFlowPosition`
  const [field, meta, helpers] = useField({
    name: fieldName,
    validate: createSimpleValidation(Yup.string().nullable())
  })
  const [columnsField, ,] = useField({
    name: `figures.${index}.numberOfOccupyingColumns`,
    validate: createSimpleValidation(Yup.string().required())
  })
  const dirty = columnsField.value?.endsWith('.5') && field.value !== meta.initialValue

  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    helpers.setValue((e.target as HTMLInputElement).value)
  }

  return (
    <Form.Group as={Row}>
      <Form.Label column sm={3}>
        記事組み込み
      </Form.Label>
      <Col sm={9} className='d-flex align-items-center'>
        <DirtyEffect dirty={dirty}>
          {Object.keys(enumValues.textFlowPositions).map((k) => (
            <Form.Check
              inline
              id={`${fieldName}-${k}`}
              key={`${fieldName}-${k}`}
              label={enumValues.textFlowPositions[k]}
              type='radio'
              name={fieldName}
              checked={k === field.value}
              value={k}
              onChange={onChange}
              disabled={inputDisabled}
            />
          ))}
        </DirtyEffect>
      </Col>
    </Form.Group>
  )
}

const FigureFilename = ({ index, inputDisabled }: { index: number; inputDisabled: boolean }) => {
  const context = useFormikContext<FormValues>()

  const validateId = (value: unknown) => {
    if (typeof value === 'number') {
      if (Number.isInteger(value) && value > 0) {
        return
      }
    } else if (typeof value === 'string') {
      if (value.startsWith('null-')) {
        return
      }
    }
    return INVALID_ID_MESSAGE
  }

  const [availableField, , availableHelper] = useField<AvailableFigure[] | undefined>('availableFigures')
  const [idField, , idHelpers] = useField({
    name: `figures.${index}.id`,
    validate: validateId
  })
  const [filenameField, filenameMeta, filenameHelpers] = useField({
    name: `figures.${index}.filename`,
    validate: createSimpleValidation(Yup.string().nullable())
  })
  const [, , locationHelpers] = useField({
    name: `figures.${index}.location`,
    validate: createSimpleValidation(Yup.string())
  })

  const dirty = filenameField.value !== filenameMeta.initialValue

  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const available = availableField.value ?? []
    const selectedValue = (e.target as HTMLSelectElement).value

    if (selectedValue === '') {
      if (idField.value.toString().startsWith('null')) {
        return
      }
      const currentFigure = {
        ...context.values.figures[index],
        id: getDummyId(),
        caption: null,
        taggedCaption: null
      }
      filenameHelpers.setValue('')
      locationHelpers.setValue('')
      availableHelper.setValue([...available, currentFigure as AvailableFigure])
    } else {
      const nextIndex = available.findIndex((f) => f.filename === selectedValue)
      if (nextIndex === undefined) {
        return
      }
      const newFigure = available[nextIndex]
      idHelpers.setValue(newFigure.id)
      filenameHelpers.setValue(newFigure.filename)
      locationHelpers.setValue(newFigure.location)
      availableHelper.setValue(available.filter((_, i) => i !== nextIndex))
    }
  }

  if (filenameField.value?.length > 0 && availableField.value === undefined) {
    return (
      <Form.Group as={Col}>
        <Form.Label column>ファイル名</Form.Label>
        <Col className='ps-3'>
          <DirtyEffect dirty={dirty}>{filenameField.value}</DirtyEffect>
        </Col>
      </Form.Group>
    )
  }

  if (filenameField.value?.length === 0 && (availableField.value?.length ?? 0) === 0) {
    return (
      <Form.Group as={Col}>
        <Form.Label column>ファイル名</Form.Label>
        <Col className='ps-3'>
          <DirtyEffect dirty={dirty}>
            <Alert variant='danger'>割り当て可能な写真・図を「複数記事追加」からアップロードしてください。</Alert>
          </DirtyEffect>
        </Col>
      </Form.Group>
    )
  }

  const filenameValues =
    filenameField.value?.length > 0 ? [{ id: idField.value, filename: filenameField.value }] : availableField.value
  const filenameOptions = (filenameValues ?? []).map(({ id, filename }) => (
    <option key={String(id)} value={filename}>
      {filename}
    </option>
  ))

  return (
    <Form.Group as={Col}>
      <Form.Label column>ファイル名</Form.Label>
      <Col className='ps-3'>
        <DirtyEffect dirty={dirty}>
          <Form.Control
            as='select'
            type='text'
            name='filename'
            size='sm'
            value={filenameField.value || ''}
            onChange={onChange}
            isInvalid={!!filenameMeta.error}
            disabled={inputDisabled}
          >
            <option value=''>画像を選択してください</option>
            {filenameOptions}
          </Form.Control>
        </DirtyEffect>
        <Form.Control.Feedback type='invalid'>{filenameMeta.error}</Form.Control.Feedback>
      </Col>
    </Form.Group>
  )
}

const CaptionField = ({
  index,
  enums,
  layoutable,
  inputDisabled
}: {
  index: number
  enums: Enums
  layoutable: Layoutable
  inputDisabled: boolean
}) => {
  const [, captionMeta, captionHelpers] = useField({
    name: `figures.${index}.caption`,
    validate: createSimpleValidation(Yup.string())
  })
  const [taggedField, taggedMeta, taggedHelpers] = useField({
    name: `figures.${index}.taggedCaption`,
    validate: createSimpleValidation(Yup.string())
  })
  const { resetCount } = useFormikReset()
  const dirty = taggedField.value !== taggedMeta.initialValue

  const onChange = (taggedContent: string, _old: string | undefined) => {
    const content = stripTags(taggedContent)
    captionHelpers.setValue(content, true)
    taggedHelpers.setValue(taggedContent, true)
  }

  return (
    <DirtyEffect dirty={dirty}>
      <TagEditor
        key={resetCount}
        initialTaggedContent={taggedField.value}
        styleTagType='caption'
        paragraphStyleName={enums.paragraphStyleName}
        setting={layoutable.setting}
        onSubmit={(_e) => undefined}
        disabled={inputDisabled}
        editable={!inputDisabled}
        onChange={onChange}
        allowLineBreak
        allowMultiParagraph={false}
        appearance='withoutSubmit'
      />
      <Form.Group as={Col}>
        <Form.Control isInvalid={!!captionMeta.error} style={{ display: 'none' }} />
        <Form.Control.Feedback type='invalid'>{captionMeta.error}</Form.Control.Feedback>
      </Form.Group>
    </DirtyEffect>
  )
}

const CaptionPositionField = ({
  index,
  enumValues,
  inputDisabled
}: {
  index: number
  enumValues: EnumValues
  inputDisabled: boolean
}) => {
  const fieldName = `figures.${index}.captionPosition`
  const [field, meta, helpers] = useField({
    name: fieldName,
    validate: createSimpleValidation(Yup.string().required())
  })
  const dirty = field.value !== meta.initialValue
  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    helpers.setValue((e.target as HTMLInputElement).value)
  }

  return (
    <DirtyEffect dirty={dirty}>
      {Object.keys(enumValues.captionPositions).map((k) => (
        <Form.Check
          inline
          id={`${fieldName}-${k}`}
          key={`${fieldName}-${k}`}
          label={enumValues.captionPositions[k]}
          type='radio'
          name={fieldName}
          checked={k === field.value}
          value={k}
          onChange={onChange}
          disabled={inputDisabled}
        />
      ))}
    </DirtyEffect>
  )
}

const FigureField = ({
  index,
  enumValues,
  inputDisabled,
  arrayHelpers,
  layoutable,
  article,
  enums,
  baseUri
}: FigureFieldProps) => {
  const [figureField, ,] = useField(`figures.${index}`)
  const showExperimentalFeatures = shouldShowExperimentalFeatures(useAuth())

  const onDeleteClick = (_e: React.MouseEvent<HTMLButtonElement>) => {
    if (figureField.value.filename?.length > 0) {
      return
    }
    arrayHelpers.remove(index)
  }

  const onViewImageClick = (_e: React.MouseEvent<HTMLButtonElement>) => {
    window.open(getFigureImageUrl(baseUri, figureField.value.id), '_blank', 'noopener')
  }

  const commonProps = { index, enumValues, inputDisabled, layoutable, article, enums }

  const textFlowPositionField = figureField.value.numberOfOccupyingColumns?.endsWith('.5') ? (
    <TextFlowPositionField {...commonProps} />
  ) : undefined

  return (
    <ListGroup.Item className='mb-2 article-detail-figure-field'>
      <Form.Group as={Row}>
        <Form.Label column sm={3}>
          種別
        </Form.Label>
        <Col sm={9}>
          <FigureTypeField {...commonProps} />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Form.Label column sm={3}>
          向き
        </Form.Label>
        <Col sm={9} className='d-flex align-items-center'>
          <OrientationField {...commonProps} />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Form.Label column sm={3}>
          使用寸法
        </Form.Label>
        <Col sm={9}>
          <Row>
            <NumberOfOccupyingColumnsField sm={6} {...commonProps} />
            <NumberOfOccupyingRowsField sm={6} {...commonProps} />
          </Row>
        </Col>
      </Form.Group>
      {textFlowPositionField}
      <Row>
        <Form.Label column sm={3}>
          位置
        </Form.Label>
        <Col>
          <Row>
            <PositionBaseField name='verticalPositionBase' {...commonProps} />
            <PositionField name='verticalPosition' {...commonProps} />
          </Row>
        </Col>
        <Col>
          <Row>
            <PositionBaseField name='horizontalPositionBase' {...commonProps} />
            <PositionField name='horizontalPosition' {...commonProps} />
          </Row>
        </Col>
      </Row>
      <Row>
        <FigureFilename index={index} inputDisabled={inputDisabled} />
      </Row>
      <Row className='mb-3'>
        <Form.Group as={Col}>
          <Form.Label column>{figureField.value.figureType === FigureType.Drawing ? '柱' : 'エトキ'}</Form.Label>
          <Col className='ps-3'>
            <CaptionField index={index} enums={enums} layoutable={layoutable} inputDisabled={inputDisabled} />
          </Col>
        </Form.Group>
      </Row>
      <Form.Group as={Row}>
        <Form.Label column sm={3}>
          配置位置
        </Form.Label>
        <Col sm={9} className='d-flex align-items-center'>
          <CaptionPositionField {...commonProps} />
        </Col>
      </Form.Group>
      <Row>
        <Col className='d-flex justify-content-end'>
          {showExperimentalFeatures && (
            <Button
              className='view-figure-image-button me-2'
              variant='secondary'
              disabled={inputDisabled || !isFigureImageViewable(figureField.value.filename)}
              size='sm'
              onClick={onViewImageClick}
            >
              画像を表示
            </Button>
          )}
          <Button
            className='delete-figure-button'
            variant='danger'
            disabled={inputDisabled || figureField.value.filename?.length > 0}
            size='sm'
            onClick={onDeleteClick}
          >
            削除
          </Button>
        </Col>
      </Row>
    </ListGroup.Item>
  )
}

export const FigureFields = ({
  inputDisabled,
  arrayHelpers,
  layoutable,
  article,
  enumValues,
  enums,
  baseUri
}: Props) => {
  const context = useFormikContext<FormValues>()
  const figures = context.values.figures || []

  const onAddClick = (_e: React.MouseEvent<HTMLButtonElement>) => {
    arrayHelpers.push(createNewFigureItem())
  }

  const commonProps = { arrayHelpers, inputDisabled, layoutable, article, enumValues, enums, baseUri }

  const figureFields = figures.map((figure, index) => {
    return <FigureField key={String(figure.id)} index={index} {...commonProps} />
  })
  const figuresNode = figures.length > 0 ? <ListGroup>{figureFields}</ListGroup> : <NoFigures />

  return (
    <>
      {figuresNode}
      {!isAreaArticleType(article.articleType) && (
        <div>
          <Button variant='primary' size='sm' onClick={onAddClick} disabled={inputDisabled}>
            追加
          </Button>
        </div>
      )}
    </>
  )
}
